<template>
    <div>
        <report-header
            :title="title"
            :isLoading="isLoading"
            :reportPagination="pagination"
        />

        <b-card id="report-card">
            <div id="report-body" class="w-100">
                <QTable
                    :data="filter !== '' ? filteredItems : items"
                    :columns="formattedColumns"
                    virtual-scroll
                    :virtual-scroll-item-size="20"
                    :virtual-scroll-sticky-size-start="20"
                    :pagination="pagination"
                    :rows-per-page-options="[0]"
                    row-key="item_id"
                    :filter="filter"
                    :hide-bottom="items.length === 0"
                    binary-state-sort
                    :loading="isLoading"
                >
                    <!-- searching -->
                    <!-- <template v-slot:top-right>
                        <b-input
                            :disabled="items.length === 0"
                            placeholder="Search"
                            v-model="filter"
                            type="search"
                            class="ml-3 mb-2"
                            style="width: 300px;"
                        />
                    </template> -->

                    <template v-slot:header-cell="{ col, sort }">
                        <th
                            @click="col.sortable ? sort(col) : null"
                            :class="{
                                'text-left': col.align === 'left',
                                'text-center': col.align === 'center',
                                'text-right': col.align === 'right',
                                sortable: col.sortable
                            }"
                        >
                            {{ col.label }}
                            <i
                                v-if="col.__thClass.includes('sort-desc')"
                                class="cus-bullet-arrow-down"
                            />
                            <i
                                v-else-if="col.__thClass.includes('sorted')"
                                class="cus-bullet-arrow-up"
                            />
                        </th>
                    </template>
                    <template v-slot:body-cell-numbering="props">
                        <td>{{ props.rowIndex + 1 }}</td>
                    </template>
                    <template v-slot:body-cell-item_name="props">
                        <q-td :props="props">
                            <item-name
                                :item="{
                                    name: props.row.item_name,
                                    encodedId: props.row.item_encoded_id
                                }"
                            />
                        </q-td>
                    </template>
                    <template
                        v-slot:bottom-row
                        v-if="items.length == 0 && !isLoading"
                    >
                        <q-tr>
                            <q-td colspan="8">No records available</q-td>
                        </q-tr>
                    </template>
                </QTable>
            </div>
        </b-card>
    </div>
</template>
<script>
import { QTable } from "quasar";
import Fuse from "fuse.js";
import Report from "@/mixins/report.js";
import "@/css/quasar-table.css";
import ItemName from "@/components/commons/item-name";
import ReportService from "@/services/report";
import CREPORT from "@/constants/report";

const SHOW_NUMBERING = false;
export default {
    components: {
        QTable,
        ItemName,
        ReportHeader: () => import("@/components/report/report-header")
    },
    mixins: [Report],
    props: {
        data: {
            type: Object
        },
        dialogs: {
            type: Object,
            default() {
                return {};
            }
        }
    },
    data() {
        return {
            indexAxios: 0,
            abortController: [],
            isLoading: false,
            pagination: this.DEFAULT_PAGINATION(),
            columns: [],
            items: [],
            filteredItems: [],
            filter: "",
            title: "Inventory Summary"
        };
    },
    computed: {
        formattedColumns() {
            let columns = [
                {
                    name: "item_name",
                    label: "Item",
                    field: row => row.item_name,
                    format: val => `${val}`,
                    align: "left",
                    sortable: true,
                    style: "width:60%"
                },
                {
                    name: "type",
                    label: "Category",
                    field: "type",
                    align: "left",
                    sortable: true,
                    style: "white-space: nowrap"
                },
                {
                    name: "category",
                    label: "Sub Category",
                    field: "category",
                    align: "left",
                    sortable: true,
                    style: "white-space: nowrap"
                },
                {
                    name: "balance",
                    label: "Balance",
                    field: "balance",
                    align: "left",
                    sortable: false,
                    style: "white-space: nowrap"
                },
                {
                    name: "unit_price",
                    label: "Unit Price",
                    field: row => row.unit_price,
                    format: (val, row) => {
                        return this._toCurrency(row.unit_price);
                    },
                    align: "left",
                    sortable: false
                },
                {
                    name: "minimum_price",
                    label: "Min Price",
                    field: row => row.minimum_price,
                    format: (val, row) => {
                        return this._toCurrency(row.minimum_price);
                    },
                    align: "left",
                    sortable: false
                },
                {
                    name: "avg_unit_cost",
                    label: "Avg Unit Cost",
                    field: row => row.avg_unit_cost,
                    format: (val, row) => {
                        return this._toCurrency(row.avg_unit_cost);
                    },
                    align: "left",
                    sortable: false
                },
                {
                    name: "value",
                    label: "Value",
                    field: row => row.value,
                    format: (val, row) => {
                        if (!isNaN(row.value)) {
                            return this._toCurrency(row.value);
                        }

                        return row.value;
                    },
                    align: "left",
                    sortable: false
                }
            ];

            if (SHOW_NUMBERING) {
                columns.unshift({
                    name: "numbering",
                    label: "#",
                    field: "n",
                    align: "right",
                    sortable: false
                });
            }

            return columns;
        }
    },
    watch: {
        filter(query) {
            const fuse = new Fuse(this.items, {
                keys: ["item_name"]
            });

            this.filteredItems = fuse.search(query);
        }
    },
    mounted() {
        let self = this;

        self.data.isShowItemCategory = true;
        self.data.isShowItemClass = true;
        self.data.isShowDateRange = false;
        self.data.isShowItemStatus = true;
        self.data.status = 2;
        self.data.filterTitle = "Filter Report";

        self.dialogs["filterDateRangeWindow"].open(self.data);

        self.$root.$on("report-filter-date-range:refresh", self.prepareData);
        self.$root.$on("inventory_summary:refresh", function () {
            self.resetPagination();
            self.items = []; // clear table (optional)
            self.isLoading = true;
            self.toggleOptions(false);
            if (self.abortController.length > 0) {
                self.abortController[self.indexAxios].abort();
            }
            self.indexAxios++;
            self.init(1, self.indexAxios);
        });
    },
    beforeDestroy: function () {
        let self = this;

        self.data.isShowItemCategory = false;
        self.data.isShowItemClass = false;
        self.data.isShowDateRange = true;
        self.data.isShowItemStatus = false;
        self.data.status = 2;
        self.data.itemClass = 0;
        self.data.category = [];
        self.toggleOptions(true);
        self.resetPagination();
        self.$root.$off("report-filter-date-range:refresh", self.prepareData);
        self.$root.$off("inventory_summary:refresh");
        if (self.abortController.length > 0) {
            self.abortController[self.indexAxios].abort();
        }
    },
    methods: {
        prepareData(params) {
            this.resetPagination();
            this.items = []; // clear table (optional)
            this.isLoading = true;
            this.toggleOptions(false);
            if (params) {
                this.data.itemClass = params.itemClass;
                this.data.category =
                    params.category.length > 0 ? params.category : [];
                this.data.status = params.status;
            }
            if (this.abortController.length > 0) {
                this.abortController[this.indexAxios].abort();
            }
            this.indexAxios++;
            this.init(0, this.indexAxios);
        },
        toggleOptions(value) {
            this.data.filterDateOption = value;
            this.data.exportOption = value;
            this.data.refreshOption = value;
        },
        timer(ms) {
            return new Promise(res => setTimeout(res, ms));
        },
        async init(isRefresh = 0, indexAxios) {
            let self = this;
            self.abortController[indexAxios] = new AbortController();

            let params = {
                page: self.pagination.page,
                perPage: self.RECORD_PER_PAGE,
                item_class: self.data.itemClass,
                category: self.data.category,
                status: self.data.status,
                refresh: isRefresh ? 1 : 0,
                signal: self.abortController[indexAxios].signal
            };

            clearInterval(self.pagination.interval);

            await self
                .fetchRecords(params)
                .then(response => {
                    if (response.message && response.message.code === 200) {
                        const reportResponse = response.resource;
                        const itemCount = reportResponse.meta.item_count;
                        const resultLength = reportResponse.results.length;

                        self.pagination.rowsNumber = itemCount;

                        if (self.pagination.page == 1) {
                            self.title = reportResponse.meta.title;
                            self.dynamicPageTitle(self.title);
                            self.pagination.lastPage =
                                Math.ceil(itemCount / self.RECORD_PER_PAGE) + 1;
                        }

                        if (resultLength > 0) {
                            // Combining new array with results
                            self.items = [
                                ...self.items,
                                ...reportResponse.results
                            ];
                        }

                        if (reportResponse.meta.item_class) {
                            self.data.itemClass =
                                reportResponse.meta.item_class;
                        }

                        self.pagination.page++;
                    } else {
                        throw new Error("Error: Unable to get the report.");
                    }

                    if (self.pagination.page < self.pagination.lastPage) {
                        self.timer(self.INTERVAL_TIME).then(() => {
                            self.init(isRefresh);
                        });
                    } else {
                        setTimeout(() => {
                            self.loadingCompleted();
                            self.toggleOptions(true);
                            self.isLoading = false;
                        }, 1000);
                    }
                })
                .catch(e => {
                    if (!self.abortController[indexAxios].signal.aborted) {
                        console.error(e);
                        self.isLoading = false;
                        self.loadingCompleted();
                        self._message(
                            "Error: Unable to get the report",
                            "danger"
                        );
                    }
                });
        },
        async fetchRecords(params) {
            this.loadingPercentages();
            return ReportService.getReport(
                CREPORT.TYPE.INVENTORY_SUMMARY,
                params
            );
        }
    }
};
</script>
